import { HeadProps, PageProps, graphql } from 'gatsby'
import React from 'react'
import { AppHead, Layout } from '../components/common'
import { LinkButton } from '../components/common/menu/linkButton'
import { BreadCrumbComponentList } from '../components/landingPage/breadCrumbComponentList'
import { LandingPageDynamicZone } from '../components/landingPage/dynamicZone/LandingPageDynamicZone'
import LandingHero from '../components/landingPage/landingHero'
import TagCategories from '../components/landingPage/tagCategories'
import { OfferTeaser } from '../components/offers'
import Cta from '../components/offers/cta'
import { Button, Link } from '../components/ui'
import { Pagination } from '../components/ui/list/Pagination'
import { ReturnTypeComposeLandingPages } from '../config/pages/composeLandingPage'
import { PaginationContext } from '../config/pages/createPaginatedPages'

type LandingPagePageContext = PaginationContext<
  ReturnTypeComposeLandingPages['offers']
> & {
  landingPageData: Queries.AllPagesQuery['allStrapiLandingPage']['nodes'][0]
}

const LandingPage = ({
  pageContext,
  data,
  location,
}: {
  pageContext: LandingPagePageContext
  data: Queries.LandingPageQuery
  location: PageProps['location']
}) => {
  const {
    group = [] as LandingPagePageContext['group'],
    landingPageData = {
      location: {
        searchLabel: '',
        latitude: '',
        longitude: '',
        radiusMeter: '',
      },
      jobTypes: null,
      contractType: '',
      title: '',
      subTitle: '',
      breadCrumb: null,
      landingSlug: '',
      landingLabel: '',
    } as unknown as LandingPagePageContext['landingPageData'],
    first = true,
    index = 0,
    last = false,
    pageCount = 1,
  } = pageContext

  let searchUrl = '/offres?'
  if (landingPageData?.location?.searchLabel) {
    searchUrl += `address=${landingPageData?.location?.searchLabel}&`
  }
  if (
    landingPageData?.location?.latitude &&
    landingPageData?.location?.longitude &&
    landingPageData?.location?.radiusMeter
  ) {
    searchUrl += `aroundLatLng=${landingPageData?.location?.latitude}, ${landingPageData?.location?.longitude}&aroundRadius=${landingPageData?.location?.radiusMeter}`
  }
  if (landingPageData?.jobTypes) {
    const jobTypeValues = landingPageData?.jobTypes?.strapi_json_value
    jobTypeValues?.map((jobTypeValue, i) => {
      searchUrl += `&filters[jobType][${i}]=${jobTypeValue}`
      return null
    })
  }
  if (landingPageData?.contractType) {
    const contractTypeFilter = landingPageData?.contractType
    searchUrl += `&filters[contractType][0]=${contractTypeFilter}`
  }

  return (
    <Layout
      careerSite={false}
      apiToken={data.site?.siteMetadata?.frontStrapiToken || undefined}
      apiUrl={data.site?.siteMetadata?.siteStrapiUrl || undefined}
      location={location}
    >
      <div className="h-full bg-very-light-blue">
        <LandingHero
          title={landingPageData?.title}
          subTitle={landingPageData?.subTitle}
        />

        <div>
          <div className="container px-4 mx-auto">
            <BreadCrumbComponentList
              breadCrumb={landingPageData?.breadCrumb}
              landingSlug={landingPageData?.landingSlug}
              landingLabel={landingPageData?.landingLabel}
            />

            {group.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-5">
                <h3 className="mb-2 font-medium text-center lg:text-xl text-md">
                  Aucune offre aujourd&apos;hui 😓
                </h3>
                <Cta />
                <Link to="/offres">
                  <Button
                    label="Voir toutes les offres"
                    color="primary"
                    invert
                  />
                </Link>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-2 ">
                  <h3 className="font-medium lg:text-xl text-md text-night">
                    {landingPageData?.title}
                  </h3>
                  <LinkButton
                    label="Voir plus"
                    href={searchUrl}
                    isExternal={false}
                  />
                </div>
                <div className="mb-2 border border-1 border-grey" />

                <div className="mb-10">
                  <div className="flex flex-col gap-5 my-5">
                    {group.map((offer) => (
                      <OfferTeaser
                        key={offer.url}
                        slug={offer.slug}
                        title={offer.title}
                        companyName={offer.companyName}
                        contractType={offer.contractType}
                        startDate={offer.startDate}
                        salary={offer.salary}
                        url={offer.url}
                        city={offer.city}
                        logo={offer.logo}
                        headerImage={offer.headerImage}
                      />
                    ))}
                  </div>

                  <section className="w-full">
                    <Pagination
                      totalPages={pageCount}
                      currentPage={index}
                      isFirst={first}
                      isLast={last}
                      baseUrl={`/${landingPageData?.landingSlug}`}
                    />
                  </section>
                </div>
              </>
            )}

            <LandingPageDynamicZone
              landingPageDynamicZones={landingPageData?.landingPageDynamicZone}
            />
            <TagCategories tagCategories={landingPageData?.tagCategories || []} />
          </div>
        </div>
      </div>
    </Layout>
  )
}

export default LandingPage

export const Head: React.FC<HeadProps<Queries.LandingPageQuery>> = ({
  data,
  pageContext,
}) => {
  const { landingPageData } = pageContext as LandingPagePageContext
  const linkCanonical = data.site?.siteMetadata?.siteUrl
    ? `${data.site?.siteMetadata?.siteUrl}/${landingPageData?.landingSlug}`
    : `https://lamacompta.co/${landingPageData?.landingSlug}`

  return (
    <AppHead
      title={
        landingPageData?.title ||
        "Offres d'emploi en cabinet d'expertise comptable"
      }
      metaDescription={
        landingPageData?.subTitle ||
        "Retrouvez toutes les offres d'emploi en cabinet d'expertise comptable sur le site de recrutement de la profession comptable"
      }
      metaTitle={
        landingPageData?.title ||
        "Offres d'emploi en cabinet d'expertise comptable"
      }
      linkCanonical={linkCanonical}
      metaImage="/lamacompta-sharing-default.jpg"
    />
  )
}

export const query = graphql`
  query LandingPage {
    site {
      siteMetadata {
        siteUrl
        frontStrapiToken
        siteStrapiUrl
      }
    }
  }
`
